import { useCallback } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import {
  CHANNEL,
  getChannelPerformanceLabel,
  isExportChannel,
  isExternalChannel,
  isMetaChannel,
} from 'src/data/channel';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { PERFORMANCE_EXTRACTION_STATUS } from 'src/data/performance';
import { WORKSPACE_ACCESS_LEVEL } from 'src/data/workspace';
import { captureMessage } from 'src/lib/sentry';
import {
  useCipEvents,
  useConfirmUnauthorizeFromChannel,
  useConnectToChannel,
  useErrorHandler,
  usePromptWorkspaceAccessLevel,
  useRestrictActionPopup,
} from 'src/services';
import {
  useBrandVoicesActions,
  useCustomerActions,
  useCustomModelsActions,
  useIsCurrentUserAdmin,
  usePerformanceActions,
  useWorkspaceActions,
} from 'src/store';
import { useAccountExtractionLoaderPopup } from 'src/views/AccountExtractionLoaderPopup';
import { useAccountsDataExtractionPopup } from 'src/views/AccountsDataExtractionPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

export const useHandlers = ({
  channel,
  onConnectSuccess,
  isTrainingModel,
  brandVoiceId,
  isFeatureLimited,
  customModel,
  onClose,
}) => {
  const performanceActions = usePerformanceActions();
  const customerActions = useCustomerActions();
  const workspaceActions = useWorkspaceActions();
  const customModelsActions = useCustomModelsActions();
  const brandVoicesActions = useBrandVoicesActions();
  const handleError = useErrorHandler();
  const cipEvents = useCipEvents();
  const { connectToChannel } = useConnectToChannel();
  const confirmUnauthorizeFromChannel = useConfirmUnauthorizeFromChannel();
  const confirmReauthorize = useConfirmReauthorize();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();
  const openRestrictActionPopup = useRestrictActionPopup();
  const openAccountExtractionLoaderPopup = useAccountExtractionLoaderPopup();
  const openAccountsDataExtractionPopup = useAccountsDataExtractionPopup();
  const promptWorkspaceAccessLevel = usePromptWorkspaceAccessLevel();
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
  });

  const refreshData = useCallback(() => {
    customerActions.refreshCurrentCustomerFeatureLimit();
    customerActions.refreshCurrentCustomerRowsAllocation();
  }, [customerActions]);

  const handleSortChange = useCallback(
    (field) => {
      performanceActions.sortAccounts(field, channel);
    },
    [performanceActions, channel]
  );

  const handleReauthorize = useCallback(async () => {
    if (!isCurrentUserAdmin) {
      openRestrictActionPopup();
      return;
    }
    const confirmed = await confirmReauthorize(channel);
    if (!confirmed) {
      return;
    }

    try {
      onClose();
      await connectToChannel({
        channel,
        reauthorize: true,
      });
    } catch (error) {
      handleError(error);
    }
  }, [
    isCurrentUserAdmin,
    confirmReauthorize,
    channel,
    openRestrictActionPopup,
    onClose,
    connectToChannel,
    handleError,
  ]);

  const handleDisconnect = useCallback(async () => {
    if (!isCurrentUserAdmin) {
      openRestrictActionPopup();
      return;
    }
    const confirmed = await confirmUnauthorizeFromChannel(channel);
    if (!confirmed) {
      return;
    }
    cipEvents.trackDisconnectChannel(channel);

    await performanceActions.disconnectFromChannel(channel);
    performanceActions.queryUserIntegrationAccounts();
    performanceActions.cancelWaitRequests(channel);

    refreshData();
    onClose();
  }, [
    confirmUnauthorizeFromChannel,
    cipEvents,
    performanceActions,
    onClose,
    isCurrentUserAdmin,
    channel,
    openRestrictActionPopup,
    refreshData,
  ]);

  const handleConnect = useCallback(
    async ({ account, isConnected, workspaceAccessLevel }) => {
      cipEvents.trackConnectAdAccounts(account.channel, account.id, 'connect');
      const shouldRecreate = customModel?.accountId === account.id;

      const brandVoice =
        brandVoicesActions.getBrandVoices().filter((bv) => bv.id === brandVoiceId)[0] || {};

      if (isTrainingModel && isConnected) {
        try {
          let cm = customModel;
          if (customModel?.id && shouldRecreate) {
            await customModelsActions.reCreateCustomModel({ customModel });
          } else {
            cm = await customModelsActions.createCustomModel({
              brandVoiceId,
              account,
              onCreateSuccess: () =>
                openAccountExtractionLoaderPopup({
                  customModelAccount: account,
                  isTrainingModel,
                  isAlreadyConnected: account.status === PERFORMANCE_EXTRACTION_STATUS.connected,
                }),
            });
          }
          if (!brandVoice.language) {
            brandVoicesActions.queryBrandVoice({ id: brandVoice.id });
          }
          refreshData();
          onConnectSuccess?.({
            customModelId: cm.id,
            brandVoiceId,
          });
        } catch (error) {
          handleError(error);
          captureMessage(`failed to ${customModel?.id ? 're' : ''}create custom model`, {
            level: 'warning',
            extra: {
              error: error.toString(),
              source: 'AdAccountsSelectionPopup -> useHandlers -> handleConnect',
            },
          });
        }
        return;
      }

      if (!isCurrentUserAdmin) {
        openRestrictActionPopup();
        return;
      }

      if (isExternalChannel(channel) || isExportChannel(channel)) {
        try {
          await performanceActions.externalConnectionConnect({
            channel,
            accountId: account.id,
            integrationAssetId: account.integrationAssetId,
            workspaceId:
              workspaceAccessLevel === WORKSPACE_ACCESS_LEVEL.onlyCurrentWorkspace
                ? workspaceActions.getCurrentWorkspaceId()
                : null,
          });
          await openAccountsDataExtractionPopup({ account });
          onConnectSuccess?.({ account });
        } catch (error) {
          handleError(error);
        }
        return;
      }

      openAccountExtractionLoaderPopup({
        account,
        isTrainingModel,
      });

      performanceActions
        .connectPerformanceCenterAccount({
          account,
          workspaceId:
            workspaceAccessLevel === WORKSPACE_ACCESS_LEVEL.onlyCurrentWorkspace
              ? workspaceActions.getCurrentWorkspaceId()
              : null,
          customModelBrandVoiceId: brandVoiceId,
        })
        .then((result) => {
          const customModelId = result?.customDatasetId;
          if (customModelId) {
            onConnectSuccess?.({
              customModelId,
              brandVoiceId,
            });
          } else {
            if (result?.status === PERFORMANCE_EXTRACTION_STATUS.connected) {
              openAccountsDataExtractionPopup({ account });
              onConnectSuccess?.({
                brandVoiceId,
              });
            }
          }
          refreshData();
        })
        .catch((error) => {
          handleError(error);
          captureMessage(`failed to connect performance center ${channel}`, {
            level: 'warning',
            extra: {
              error: error.toString(),
              source: 'AdAccountsSelectionPopup -> useHandlers -> handleConnect',
            },
          });
        });
    },
    [
      cipEvents,
      customModel,
      isTrainingModel,
      isCurrentUserAdmin,
      openAccountExtractionLoaderPopup,
      customModelsActions,
      performanceActions,
      workspaceActions,
      brandVoiceId,
      refreshData,
      onConnectSuccess,
      handleError,
      openRestrictActionPopup,
      openAccountsDataExtractionPopup,
      channel,
      brandVoicesActions,
    ]
  );

  const handleAccountClick = useCallback(
    async (account, isConnected) => {
      const currentWorkspaces = workspaceActions.getCurrentWorkspaces();
      if (!account) {
        return;
      }

      if (isFeatureLimited && !isConnected) {
        openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
        return;
      }

      // Request workspace access level for this account - only if not connected and customer has more than 1 workspace
      let workspaceAccessLevel;
      if (!isConnected && currentWorkspaces.length > 1) {
        workspaceAccessLevel = await promptWorkspaceAccessLevel(account);
        if (!workspaceAccessLevel) {
          return;
        }
      }

      // Start "connection" flow
      handleConnect({
        account,
        isConnected,
        workspaceAccessLevel,
      });

      onClose();
    },
    [
      isFeatureLimited,
      workspaceActions,
      promptWorkspaceAccessLevel,
      handleConnect,
      onClose,
      openUpgradePopup,
    ]
  );

  return { handleSortChange, handleReauthorize, handleDisconnect, handleAccountClick };
};

const useConfirmReauthorize = () => {
  const confirmPopup = useConfirmPopup();

  const alignChannelLabel = (channel) =>
    isMetaChannel(channel)
      ? `${getChannelPerformanceLabel(CHANNEL.facebook)}, ${getChannelPerformanceLabel(
          CHANNEL.facebookPages
        )} and ${getChannelPerformanceLabel(CHANNEL.instagram)}`
      : getChannelPerformanceLabel(channel);

  return useCallback(
    (channel) => {
      return confirmPopup({
        mode: CONFIRM_POPUP_MODE.confirmation,
        title: `Re-authorize integration?`,
        content: `Are you sure you want to re-authorize ${alignChannelLabel(channel)} integration?`,
        buttons: {
          confirm: 'Re-authorize',
        },
        withReject: true,
      })
        .then(() => true)
        .catch(() => false);
    },
    [confirmPopup]
  );
};
